import type { StyleRule } from '@archipro-design/aria';
import {
    labelClassName,
    captionClassName,
    buttonClassName,
    imageClassName,
} from '@archipro-design/aria';
import { pxToRem, pxArrayToRem } from '@archipro-design/aria';

export const CompanyImages: StyleRule = () => ({});

export const ImageWrap: StyleRule = () => ({
    position: `relative`,
});

interface PlacementGridProps {
    isPortraitOnly?: boolean;
    placementCount: number;
}

const PORTRAIT_SIZE_DESKTOP = 480;
const GRID_GAP = 25;

export const SidebarReadMore: StyleRule = () => ({
    display: `flex`,
    flexDirection: `column`,
    alignItems: `flex-start`,
    [`& .${labelClassName}`]: {
        '& > :first-child': {
            marginTop: 0,
        },
        '& > :last-child': {
            marginBottom: 0,
        },
    },
    [`& > .${buttonClassName}`]: {
        marginTop: pxToRem(4),
        padding: 0,

        [`&:hover`]: {
            backgroundColor: 'transparent',
        },
    },

    [`& > .${buttonClassName} .${labelClassName}`]: {
        lineHeight: `1em`,
    },
});

export const PhotoViewerSlide: StyleRule = () => ({
    maxHeight: `100%`,
    textAlign: 'center',
});

export const PhotoViewerCaption: StyleRule = () => ({
    textAlign: 'center',
    margin: `${pxToRem(16)} auto 0`,
    maxWidth: pxToRem(600),
    [`& .${captionClassName}`]: {
        display: 'block',
        minHeight: pxToRem(18),
    },
});

export const PhotoViewerDialog: StyleRule = ({ theme }) => ({
    padding: 0,
    maxWidth: '100vw !important',
    maxHeight: '100vh',
    borderRadius: '0 !important',
    '& button': {
        background: 'transparent',
    },
    '& .ui-dialog__content': {
        borderRadius: 0,
        border: 'none',
        margin: 0,
        padding: 0,
        maxHeight: `100vh`,
        maxWidth: '100%',
        background: theme.siteVariables.colors.white['100A'],
    },

    '& .ui-dialog__headerAction': {
        position: 'fixed',
        top: pxToRem(23),
        right: pxToRem(30),
        zIndex: 30,
        outline: 'none',
        border: 0,
        padding: 0,
    },
    '& .ui-dialog__footer': {
        display: 'none',
    },
});

export const PlacementGridContainer: StyleRule = ({ theme }) => ({
    [theme.screen.laptop]: {
        maxWidth: pxToRem(1490),
        margin: `0 auto`,
        width: `100%`,
    },
});

export const PlacementGrid: StyleRule<PlacementGridProps> = ({
    isPortraitOnly,
    placementCount,
}) => {
    const gridLayout = {
        '& > div:nth-child(1)': {
            gridColumnStart: 2,
            gridColumnEnd: 4,
        },
        '& > div:nth-child(2)': {
            gridColumnStart: 1,
            gridColumnEnd: 2,
        },
        '& > div:nth-child(3)': {
            gridColumnStart: 2,
            gridColumnEnd: 3,
        },
    };

    return {
        gridGap: pxToRem(GRID_GAP),
        ...(!isPortraitOnly && {
            margin: pxArrayToRem([0, -8]),
            ...gridLayout,
        }),
        ...(isPortraitOnly && {
            margin: `0 auto`,
            width: pxToRem(
                placementCount * PORTRAIT_SIZE_DESKTOP +
                    (placementCount - 1) * GRID_GAP
            ),
        }),
    };
};

export const ViewMore: StyleRule<{
    isPortraitOnly?: boolean;
}> = ({ isPortraitOnly }) => {
    return {
        [`&.${buttonClassName}`]: {
            marginTop: pxToRem(16),
            textDecoration: `underline`,

            ...(isPortraitOnly && {
                display: `block`,
                marginLeft: `auto`,
            }),
        },
    };
};

export const companyImagesMobile: StyleRule = () => {
    return {
        [`& > ${imageClassName}`]: {
            margin: 'inherit',
        },
    };
};

export const MobileCompanyImageWrapper: StyleRule = ({
    theme,
    placementCount,
}) => {
    return {
        overflow: 'hidden',
        marginBottom: placementCount > 1 ? 0 : pxToRem(35),
        '& .swiper-pagination': {
            justifyContent: 'center',
            marginTop: pxToRem(16),
            fontSize: pxToRem(14),
            position: 'relative',
            bottom: 0,
        },
        '& .swiper-pagination-current': {
            marginRight: pxToRem(4),
        },
        '& .swiper-pagination-total': {
            marginLeft: pxToRem(4),
        },
        '& div': {
            display: 'flex',
            maxHeight: pxToRem(252),
        },

        '& .swiper-slide': {
            justifyContent: 'center',
        },

        '& img': {
            height: pxToRem(252),
            width: 'unset',
            maxHeight: 'unset',
            maxWidth: 'unset',
            backgroundColor: theme.siteVariables.colors.white['300'],
        },
    };
};

export const ImageLoadingSpinner: StyleRule = () => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    margin: 'auto',
    left: 0,
    right: 0,
    zIndex: 1,
    transition: 'transform 0.2s ease-in',
});

export const PageSectionImages: StyleRule = () => ({
    gridGap: 0,
});

export const StackContainer: StyleRule<{ scrolled?: boolean }> = ({
    scrolled,
}) => ({
    padding: pxArrayToRem([231, 0]),
    gridGap: pxToRem(197),

    opacity: scrolled ? 1 : 0,
});

export const StackSlideContainer: StyleRule = () => ({
    paddingTop: pxToRem(197),
    marginTop: pxToRem(-197),
});
